import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/ui/button";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/ui/form";
import { Input } from "@/ui/input";
import {
    DQ_CHECK_BY_NAME,
    buildRangeDqValue,
    buildValueDqValue,
} from "@/features/adp/constants/dataQualityChecks";
import type { DqCheckName } from "@/features/adp/constants/dataQualityChecks";

const rangeSchema = z.object({
    min: z.string().trim().min(1),
    max: z.string().trim().min(1),
});

const valueSchema = z.object({
    value: z.string().trim().min(1),
});

type DqCheckFormValues = {
    min: string;
    max: string;
    value: string;
};

const DEFAULT_VALUES: DqCheckFormValues = { min: "", max: "", value: "" };

interface DqCheckDialogProps {
    /** The check the user just selected from the multiselect. `null` keeps the dialog closed. */
    checkName: DqCheckName | null;
    /** Save → returns the formatted `range?…` or `prefix?…` token. */
    onConfirm: (value: string) => void;
    /** Cancel / close → caller should remove the pending check from the column's selection. */
    onCancel: () => void;
}

const DqCheckDialog = ({ checkName, onConfirm, onCancel }: DqCheckDialogProps) => {
    const open = checkName != null;
    const def = checkName ? DQ_CHECK_BY_NAME[checkName] : null;
    const isRange = def?.modal === "range";
    const isValueModal = def?.modal === "value";

    const form = useForm<DqCheckFormValues>({ defaultValues: DEFAULT_VALUES });
    const watched = form.watch();

    useEffect(() => {
        if (open) form.reset(DEFAULT_VALUES);
    }, [open, checkName, form]);

    const isSaveDisabled = useMemo(() => {
        if (isRange) {
            return !rangeSchema.safeParse({ min: watched.min, max: watched.max }).success;
        }
        if (isValueModal) {
            return !valueSchema.safeParse({ value: watched.value }).success;
        }
        return true;
    }, [isRange, isValueModal, watched.min, watched.max, watched.value]);

    const handleOpenChange = (next: boolean) => {
        if (!next) onCancel();
    };

    const handleSave = form.handleSubmit((data) => {
        if (!checkName) return;
        if (isRange) {
            const parsed = rangeSchema.safeParse({ min: data.min, max: data.max });
            if (!parsed.success) return;
            onConfirm(buildRangeDqValue(parsed.data.min, parsed.data.max));
            return;
        }
        if (isValueModal) {
            const parsed = valueSchema.safeParse({ value: data.value });
            if (!parsed.success) return;
            onConfirm(buildValueDqValue(checkName, parsed.data.value));
        }
    });

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{checkName}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={handleSave}>
                        <DialogBody>
                            {isRange && (
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="min"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Min</FormLabel>
                                                <FormControl>
                                                    <Input {...field} placeholder="Enter min" autoFocus />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="max"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Max</FormLabel>
                                                <FormControl>
                                                    <Input {...field} placeholder="Enter max" />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            )}

                            {isValueModal && (
                                <FormField
                                    control={form.control}
                                    name="value"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Value</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Enter value" autoFocus />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            )}
                        </DialogBody>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isSaveDisabled}>
                                Save
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default DqCheckDialog;
